import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, MailOpen, MousePointerClick, XOctagon, Send, AlertTriangle, RefreshCw } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { BRAND_COLORS } from "../lib/bookingStatus";
import { deDateMedium } from "../lib/dateFormat";

const EVENT_META = {
  processed: { label: "Verarbeitet", color: BRAND_COLORS.navy, Icon: Send },
  delivered: { label: "Zugestellt", color: "#3A7A47", Icon: Mail },
  open: { label: "Geöffnet", color: BRAND_COLORS.gold, Icon: MailOpen },
  click: { label: "Geklickt", color: "#8A5A2D", Icon: MousePointerClick },
  bounce: { label: "Bounce", color: "#B04A3A", Icon: XOctagon },
  dropped: { label: "Verworfen", color: "#B04A3A", Icon: XOctagon },
  deferred: { label: "Verzögert", color: "#6B6B6B", Icon: AlertTriangle },
  spamreport: { label: "Spam gemeldet", color: "#8B0000", Icon: AlertTriangle },
};

function EventBadge({ event }) {
  const meta = EVENT_META[event] || { label: event, color: BRAND_COLORS.navy, Icon: Mail };
  const Icon = meta.Icon;
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10.5px] tracking-[0.14em] uppercase font-medium" style={{ background: `${meta.color}18`, color: meta.color, borderLeft: `2px solid ${meta.color}` }}>
      <Icon className="w-3 h-3" strokeWidth={2} />
      {meta.label}
    </span>
  );
}

function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "gerade eben";
  if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min.`;
  if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std.`;
  if (diff < 604800) return `vor ${Math.floor(diff / 86400)} Tagen`;
  return deDateMedium(d);
}

export default function TeamEmails() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total_30d: 0, by_event: [] });
  const [emailFilter, setEmailFilter] = useState("");
  const [eventFilter, setEventFilter] = useState("");
  const [loading, setLoading] = useState(false);

  // Filter werden explizit übergeben („Anwenden"/Enter) — `load` bleibt stabil,
  // der Mount-Effekt feuert nicht bei jedem Tastendruck im Suchfeld.
  const load = useCallback(async (email, event) => {
    setLoading(true);
    try {
      const params = {};
      if ((email || "").trim()) params.email = email.trim();
      if (event) params.event = event;
      const [r, s] = await Promise.all([
        api.get("/team/email-events", { params }),
        api.get("/team/email-events/stats"),
      ]);
      setRows(r.data);
      setStats(s.data);
    } catch (e) {
      console.error("email-events load failed", e);
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load("", ""); }, [load]);

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="team-emails">
      <div className="mb-10 flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="label-caps mb-3">Weissblom Team</div>
          <h1 className="font-display text-4xl md:text-5xl text-navy">E-Mail-Zustellung</h1>
          <p className="text-[14px] text-navy-dark/70 mt-2 max-w-2xl">
            Zeigt alle SendGrid-Ereignisse der letzten Wochen — von der Zustellung bis zum Klick. Aktualisiert sich, sobald SendGrid einen Webhook liefert.
          </p>
        </div>
        <button
          data-testid="email-events-refresh"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 border border-navy text-navy px-4 py-2.5 text-[11px] tracking-[0.18em] uppercase hover:bg-navy hover:text-ivory transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Neu laden
        </button>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8" data-testid="email-events-stats">
        <div className="bg-white border border-navy/10 p-4">
          <div className="label-caps mb-1">Letzte 30 Tage</div>
          <div className="font-display text-3xl text-navy">{stats.total_30d}</div>
        </div>
        {stats.by_event.slice(0, 4).map((s) => {
          const meta = EVENT_META[s.event] || { label: s.event, color: BRAND_COLORS.navy };
          return (
            <div key={s.event} className="bg-white border border-navy/10 p-4" style={{ borderTopColor: meta.color, borderTopWidth: 2 }}>
              <div className="label-caps mb-1" style={{ color: meta.color }}>{meta.label}</div>
              <div className="font-display text-3xl text-navy">{s.count}</div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <input
          data-testid="email-filter-input"
          value={emailFilter}
          onChange={(e) => setEmailFilter(e.target.value)}
          placeholder="E-Mail suchen…"
          className="bg-white border border-navy/15 px-3 py-2 text-[13px] focus:border-gold focus:outline-none w-64"
          onKeyDown={(e) => { if (e.key === "Enter") load(emailFilter, eventFilter); }}
        />
        <select
          data-testid="event-filter-select"
          value={eventFilter}
          onChange={(e) => setEventFilter(e.target.value)}
          className="bg-white border border-navy/15 px-3 py-2 text-[13px] focus:border-gold focus:outline-none"
        >
          <option value="">Alle Ereignisse</option>
          {Object.entries(EVENT_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
        <button
          data-testid="apply-filter-btn"
          onClick={() => load(emailFilter, eventFilter)}
          className="border border-navy text-navy px-4 py-2 text-[11px] tracking-[0.18em] uppercase hover:bg-navy hover:text-ivory transition-colors"
        >
          Anwenden
        </button>
      </div>

      {/* Events table */}
      <div className="bg-white border border-navy/10 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-navy/10">
              <th className="label-caps text-left px-5 py-3">Ereignis</th>
              <th className="label-caps text-left px-5 py-3">Empfänger</th>
              <th className="label-caps text-left px-5 py-3">Betreff</th>
              <th className="label-caps text-left px-5 py-3">Detail</th>
              <th className="label-caps text-left px-5 py-3">Zeitpunkt</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.sg_event_id || `${r.sg_message_id || "m"}-${i}`} className="border-b border-navy/5 last:border-b-0 hover:bg-ivory/50 transition-colors" data-testid={`email-event-row-${i}`}>
                <td className="px-5 py-3"><EventBadge event={r.event} /></td>
                <td className="px-5 py-3 text-[13px] text-navy-dark">{r.email}</td>
                <td className="px-5 py-3 text-[13px] text-navy-dark/80">{r.subject || <span className="text-navy-dark/40 italic">—</span>}</td>
                <td className="px-5 py-3 text-[12px] text-navy-dark/70 max-w-xs truncate" title={r.url || r.reason || ""}>{r.url || r.reason || ""}</td>
                <td className="px-5 py-3 text-[12px] text-navy-dark/60" title={r.at}>{relTime(r.at)}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-6 py-16 text-center text-[13px] text-navy-dark/50">
                Noch keine Ereignisse eingetroffen. Konfigurieren Sie den <em>Event Webhook</em> in SendGrid → Settings → Mail Settings.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-6 p-4 border border-gold/40 bg-ivory/40 text-[12px] text-navy-dark/80 leading-relaxed" data-testid="webhook-hint">
        <div className="label-caps mb-1" style={{ color: BRAND_COLORS.gold }}>Webhook-URL</div>
        <code className="text-[12px] font-mono text-navy break-all">{window.location.origin}/api/sendgrid/webhook</code>
        <p className="mt-2 text-navy-dark/60">
          Tragen Sie diese URL in SendGrid unter <em>Settings → Mail Settings → Event Webhook</em> ein und aktivieren Sie mindestens <em>Processed</em>, <em>Delivered</em>, <em>Opened</em>, <em>Clicked</em>, <em>Bounced</em>.
        </p>
      </div>
    </div>
  );
}
